import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FilePlus2, Files, Building2, Users,
  Layout, Settings, LogOut, ChevronRight, Menu, X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { LOGO_URL } from "@/lib/brand";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/transactions/new", label: "New Offer", icon: FilePlus2, tid: "nav-new-offer" },
  { to: "/transactions", label: "Transactions", icon: Files, tid: "nav-transactions" },
  { to: "/properties", label: "Properties", icon: Building2, tid: "nav-properties" },
  { to: "/clients", label: "Clients", icon: Users, tid: "nav-clients" },
  { to: "/templates", label: "Templates", icon: Layout, tid: "nav-templates" },
  { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  return (
    <>
      <div className="px-6 pt-6 pb-4 border-b border-white/10">
        <img src={LOGO_URL} alt="Moaglee Commercial Real Estate"
             className="w-full max-h-28 object-contain" data-testid="brand-logo" />
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {nav.map(({ to, label, icon: Icon, tid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={tid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-colors duration-150 ${
                isActive
                  ? "bg-brass text-navy-900 font-semibold"
                  : "text-white hover:bg-white/10 hover:text-brass"
              }`
            }
            end={to === "/dashboard"}
          >
            <Icon size={16} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 p-4">
        <div className="text-[11px] uppercase tracking-widest text-white/50 mb-1">Signed in</div>
        <div className="text-sm text-white truncate" data-testid="user-name">{user?.full_name}</div>
        <div className="text-xs text-white/60 truncate">{user?.brokerage_name || user?.email}</div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="mt-3 w-full flex items-center justify-between px-3 py-2 rounded-sm text-sm bg-brass text-navy-900 font-semibold hover:bg-brass-600 hover:text-white transition-colors duration-150"
        >
          <span className="flex items-center gap-2"><LogOut size={14} /> Sign out</span>
          <ChevronRight size={14} />
        </button>
      </div>
    </>
  );
}

export default function AppLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Auto-close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex w-[260px] shrink-0 bg-navy text-white flex-col sticky top-0 h-screen"
        data-testid="app-sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          data-testid="mobile-drawer-overlay"
          onClick={() => setMobileOpen(false)}
        >
          <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" />
          <aside
            className="absolute left-0 top-0 bottom-0 w-[80%] max-w-[300px] bg-navy text-white flex flex-col animate-fade-in"
            data-testid="mobile-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-brass p-1"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-close"
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <div className="border-b border-border bg-white sticky top-0 z-30">
          <div className="px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
            <button
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-sm bg-navy text-white hover:bg-navy-800 transition-colors"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <img
              src={LOGO_URL}
              alt="Moaglee"
              className="md:hidden h-10 w-auto bg-navy rounded-sm px-2 py-1 object-contain"
            />
            <div className="hidden md:block text-xs uppercase tracking-[0.2em] text-slate-500">
              Commercial Real Estate · Offer Desk
            </div>
            <div className="text-[10px] sm:text-[11px] text-slate-500 font-mono shrink-0">v0.1 · MVP</div>
          </div>
        </div>
        <div className="px-4 sm:px-8 py-6 sm:py-8 animate-fade-in">{children}</div>
      </main>

      <Toaster richColors position="top-right" />
    </div>
  );
}
